import clsx from "clsx";
import { useState, type ReactNode } from "react";
import WorkerAvatar from "@/components/worker/WorkerAvatar";
import {
  AGENTIC_THOUGHT_BODY_CLASS,
  AGENTIC_THOUGHT_SUMMARY_CLASS,
} from "@/components/ui/agentic-shared/agenticSurface";

export type AgenticThoughtBlockProps = {
  children: ReactNode;
  agentName?: string;
  title?: string;
  /** Initial open state for collapsible blocks (uncontrolled). */
  defaultOpen?: boolean;
  /** When false, renders a static bubble (no Show/Hide toggle). */
  collapsible?: boolean;
  className?: string;
};

export function AgenticThoughtBlock({
  children,
  agentName = "Agent",
  title = "Reasoning",
  defaultOpen = false,
  collapsible = true,
  className,
}: AgenticThoughtBlockProps) {
  const [open, setOpen] = useState(defaultOpen);

  if (!collapsible) {
    return (
      <div
        className={clsx("relative max-w-prose", className)}
        data-testid="agentic-thought-block"
      >
        <div className={clsx(AGENTIC_THOUGHT_BODY_CLASS, "ring-1 ring-white/[0.06]")}>
          <div className="mb-1.5 flex items-center gap-1.5">
            <WorkerAvatar seed={agentName} size={18} user className="shrink-0" />
            <span className="text-[11px] font-medium text-slate-500 dark:text-slate-400">
              {title}
            </span>
          </div>
          <p className="leading-relaxed whitespace-pre-line text-slate-200">{children}</p>
        </div>
      </div>
    );
  }

  return (
    <details
      className={clsx("group relative max-w-prose", className)}
      open={open}
      onToggle={(event) => setOpen(event.currentTarget.open)}
      data-testid="agentic-thought-block"
    >
      <summary className="cursor-pointer list-none [&::-webkit-details-marker]:hidden">
        <div className={AGENTIC_THOUGHT_SUMMARY_CLASS}>
          <div className="flex items-center gap-1.5">
            <WorkerAvatar seed={agentName} size={18} user className="shrink-0" />
            <span className="text-[11px] font-medium text-slate-500 dark:text-slate-400">
              {title}
            </span>
            <span className="ml-auto text-[10px] text-slate-400 group-open:hidden dark:text-slate-500">
              Show
            </span>
            <span className="ml-auto hidden text-[10px] text-slate-400 group-open:inline dark:text-slate-500">
              Hide
            </span>
          </div>
        </div>
      </summary>
      <div className={clsx("mt-1", AGENTIC_THOUGHT_BODY_CLASS)}>
        <p className="leading-relaxed whitespace-pre-line">{children}</p>
      </div>
    </details>
  );
}
